/**
 * App lifecycle: one place that hears the native app going to the background and coming back.
 *
 * iOS suspends the WebView in the background: timers stop, sockets go half-open, and a live stream can sit on a
 * dead connection for minutes after the app returns. Modules that need to react (the agent stream reconnects,
 * the queue refreshes, the push token re-registers, dictation stops) subscribe here instead of each adding its
 * own `@capacitor/app` listener.
 *
 *   const off = onAppResume(({ awayMs }) => reconnect())
 *   onAppPause(() => stopDictation())
 *
 * Native only (`pause` / `resume` from `@capacitor/app`); on the web nothing fires, so web behaviour is unchanged.
 * `isAppActive` is a ref that is false while the app is in the background.
 */
import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { readonly, ref } from 'vue'
import { isNativePlatform } from '@/utils/platform'

export interface ResumeInfo {
    /** How long the app was in the background (0 if we never saw it leave). */
    awayMs: number
}

type PauseFn = () => void
type ResumeFn = (info: ResumeInfo) => void

const pauseFns = new Set<PauseFn>()
const resumeFns = new Set<ResumeFn>()
const active = ref(true)
/** False while the app is in the background. */
export const isAppActive = readonly(active)

let pausedAt = 0
let installed = false

export function onAppPause(fn: PauseFn): () => void {
    pauseFns.add(fn)
    return () => {
        pauseFns.delete(fn)
    }
}

export function onAppResume(fn: ResumeFn): () => void {
    resumeFns.add(fn)
    return () => {
        resumeFns.delete(fn)
    }
}

function run<T extends unknown[]>(fns: Set<(...a: T) => void>, ...args: T): void {
    for (const fn of [...fns]) {
        try {
            fn(...args)
        } catch (err) {
            console.error('[lifecycle] listener failed', err)
        }
    }
}

/** Exposed for tests and the dev lab; the App plugin calls these on device. */
export function emitAppPause(now = Date.now()): void {
    if (!active.value) return
    active.value = false
    pausedAt = now
    run(pauseFns)
}

export function emitAppResume(now = Date.now()): void {
    const awayMs = pausedAt ? Math.max(0, now - pausedAt) : 0
    pausedAt = 0
    active.value = true
    run(resumeFns, { awayMs })
}

/** Call once at boot (main.ts). Native only. */
export async function initAppLifecycle(): Promise<void> {
    if (installed || !isNativePlatform() || !Capacitor.isPluginAvailable('App')) return
    installed = true
    try {
        await App.addListener('pause', () => emitAppPause())
        await App.addListener('resume', () => emitAppResume())
    } catch {
        installed = false
    }
}

/** Test-only. */
export function __resetLifecycleForTests(): void {
    pauseFns.clear()
    resumeFns.clear()
    active.value = true
    pausedAt = 0
}
